import {
    applyMiddleware,
    bindActionCreators,
    combineReducers,
    compose,
    createStore,
    Middleware,
} from "redux";
import { createEpicMiddleware } from "redux-observable";
// @ts-ignore
import { unstable_trace as trace } from "scheduler/tracing";

import { Action, actionCreators } from "../actions";
import { InitialState, reducers, RootState } from "../reducers";

import { ExtraArguments, RootEpic } from "./types";

const rootReducer = combineReducers<RootState, Action>(reducers);

export const bootstrapStore = (
    extraArgs: ExtraArguments,
    rootEpic: RootEpic,
    initialState?: InitialState
) => {
    const {
        redux_debug: allow = false,
        async_redux: async = false,
        redux_logger: reduxLogger = false,
    } = initialState ? initialState.persist.debug_flags : {};
    let logger = reduxLogger;

    const composeEnhancers =
        (allow && window && (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

    const epicMiddleware = createEpicMiddleware<Action, Action, RootState>({
        dependencies: extraArgs,
    });

    let n = 0;
    function configureStore() {
        let listeners: (() => void)[] = [];
        let timer: ReturnType<typeof requestAnimationFrame> | undefined;
        let batchedActions: Action[] = [];
        const notify = () => {
            n > 99 && (n = 0);
            const i = n++;
            const name = batchedActions.map(x => x.type).join(", ");
            window.performance.mark("redux_notify_start_" + i);
            const start = performance.now();
            trace("🦉 " + name, start, () => {
                // Interactions in Profile tab in React plugin
                listeners.forEach(l => l());
            });
            const end = performance.now();
            window.performance.mark("redux_notify_end" + i);
            window.performance.measure(
                // notifying react may cause new actions to be triggered (like in componentDidUpdate)
                "🦉 " + name + " [react update " + i + "]",
                "redux_notify_start_" + i,
                "redux_notify_end" + i
            );
            const title = `${name} (${(end - start).toFixed(3)}ms)`;
            if (logger) {
                console.groupCollapsed(title);
                console.info(...batchedActions);
                console.groupEnd();
            }
            batchedActions = [];
        };
        const notifyMiddleware: Middleware = store => next => (action: Action) => {
            n > 99 && (n = 0);
            const i = n++;
            window.performance.mark("redux_action_start_" + i);
            const result = next(action);
            batchedActions.push(action);
            const {
                async_redux: async = false,
                async_redux_idle: idle = false,
                async_redux_sparse: sparse = false,
                redux_logger: reduxLogger = false,
            } = store.getState().persist.debug_flags;
            logger = reduxLogger;
            timer &&
                (sparse
                    ? clearTimeout(timer)
                    : idle
                    ? window.cancelIdleCallback(timer)
                    : cancelAnimationFrame(timer));
            if (!async || ("renderImmediate" in action && action.renderImmediate)) {
                notify();
            } else {
                timer = sparse
                    ? window.setTimeout(notify, 1000)
                    : idle
                    ? window.requestIdleCallback(notify)
                    : requestAnimationFrame(notify);
            }
            window.performance.mark("redux_action_end_" + i);
            window.performance.measure(
                // this will include time required to render if action is immediate
                "🦆 " + action.type + " [redux action " + i + "]",
                "redux_action_start_" + i,
                "redux_action_end_" + i
            );
            return result;
        };

        // compose enhancers
        const enhancer = composeEnhancers(
            applyMiddleware(...[epicMiddleware, ...(async || logger ? [notifyMiddleware] : [])])
        );

        // create store
        const store = createStore(rootReducer, initialState, enhancer);

        if (async || logger) {
            store.subscribe = listenter => {
                listeners = [...listeners, listenter];
                return function unsubscribe() {
                    listeners = listeners.filter(x => x !== listenter);
                };
            };
        }

        epicMiddleware.run(rootEpic);
        return store;
    }

    // pass an optional param to rehydrate state on app start
    const store = configureStore();

    const actions = bindActionCreators(actionCreators, store.dispatch);

    return { store, actions, epicMiddleware };
};
